// Deep-link query-param reading for the `/send-avatar` route.
//
// Why this exists: the account share link the dashboard hands out is
// `https://<host>/send-avatar?clientId=<digits>`. When a user retypes or pastes
// it WITHOUT the scheme, the browser omnibox has to decide "URL or search?" and
// lowercases the query while resolving the bare host — `clientId` arrives as
// `clientid`. `URLSearchParams.get()` is case-sensitive, so the read returned
// null, the `clientIds` org lookup never ran, and the "Sending Avatar to:"
// banner rendered blank on an otherwise healthy page. Silent failure: the link
// looks like it worked.
//
// The generator side is fixed (bodyscanner_dashboard renders the canonical
// https link with a copy button), but links already in circulation — printed QR
// cards, links sitting in client inboxes, entries in a client's own CRM — keep
// arriving mangled for as long as they are in use. This is the receiving-end
// net for those.

// Deliberately `clientId` ONLY. See `getClientIdParam` below for why sessionId
// and redirectUrl are excluded.
const CLIENT_ID_PARAM = "clientId";

/**
 * Read `clientId` from a deep-link query string, tolerating any capitalisation
 * of the KEY (`clientId`, `clientid`, `CLIENTID`).
 *
 * Safe to relax here because the VALUE is all digits — lowercasing the query
 * cannot corrupt it, so a case-insensitive key match fully recovers the link.
 *
 * NOT extended to `sessionId` or `redirectUrl`, and that asymmetry is load-
 * bearing. Their values are case-SENSITIVE (a `widget_sessions` document id, a
 * URL with a path and token), so the same lowercasing destroys the value too and
 * no key matching can bring it back. Relaxing the key for those would make a
 * mangled link resolve the org name, look healthier, and then fail later at the
 * Firestore read — a worse failure than the honest early one, and harder to
 * diagnose. Those links are machine-generated into widgets and emails and are
 * clicked rather than retyped, so they are not exposed to this in practice.
 *
 * Exact-case matches win, so a well-formed link never takes the fallback path.
 *
 * @param search `window.location.search` (leading `?` optional)
 * @returns the clientId, or null when absent under any capitalisation
 */
export function getClientIdParam(search: string): string | null {
  const params = new URLSearchParams(search);

  const exact = params.get(CLIENT_ID_PARAM);
  if (exact !== null) return exact;

  // Build a lowercase-keyed view rather than iterating `params.entries()`:
  // tsconfig targets es5 without `downlevelIteration`, so `for...of` over a
  // URLSearchParams iterator does not compile. `forEach` is a plain method and
  // needs no iterator protocol.
  const byLowerKey: { [key: string]: string } = {};
  params.forEach((value, key) => {
    const lower = key.toLowerCase();
    // First occurrence wins, matching `URLSearchParams.get()` for repeated keys.
    if (!Object.prototype.hasOwnProperty.call(byLowerKey, lower)) {
      byLowerKey[lower] = value;
    }
  });

  const lowerTarget = CLIENT_ID_PARAM.toLowerCase();
  return Object.prototype.hasOwnProperty.call(byLowerKey, lowerTarget)
    ? byLowerKey[lowerTarget]
    : null;
}

/**
 * True when `search` carries a clientId that ONLY a case-insensitive read finds
 * — i.e. the link reached us mangled and was rescued. Callers use this to log
 * the rescue; it is not part of resolving the value.
 */
export function wasClientIdCaseMangled(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get(CLIENT_ID_PARAM) === null && getClientIdParam(search) !== null
  );
}
